import { summarizeBundle4ShadowLine } from "./bundle4Shadow";
import { summarizeBundle5ShadowLine } from "./bundle5Shadow";
import { summarizeBundle20ExitEnforceLine } from "./bundle20ExitEnforce";
import { summarizePortfolioCouplingLine } from "./portfolioCoupling";
import {
  formatReviewReadinessLayers,
  resolveReviewReadinessClient,
  reviewReadinessTone,
  type ReviewReadiness,
} from "./reviewReadiness";
import type { CycleOperatorSummary, EntryObservabilityResponse } from "./types";

/** Observability window — sync with `data/improvement-cycle.json` `createdAfter`. */
export const FIXED_CYCLE_CREATED_AFTER = "2026-06-04T00:09:58.000Z";

/** Legacy 500 target when protocol is not profit-v1. */
export const LEGACY_CYCLE_REQUIRED_TRADES = 500;

/**
 * Sync with `data/improvement-cycle.json` → `reviewProtocol.nextReviewAtFills`.
 * null when phase is awaiting-improvement-ship. Enforced in `tradeCycle.test.ts`.
 */
export const PROFIT_V1_NEXT_REVIEW_AT_FILLS: number | null = 1655;

/** Used when /cycle/operator-summary fails before any successful fetch (sync with improvement-cycle.json). */
export const PROFIT_V1_DASHBOARD_FALLBACK: CycleOperatorSummary = {
  protocolVersion: "profit-v1",
  createdAfter: FIXED_CYCLE_CREATED_AFTER,
  operatingMode: "profit_shadow",
  entryGuardMode: "shadow",
  legacy500Retired: true,
  fills: null,
  fillsMetric: "protocol_entry_fills",
  fillsSource: null,
  fillsDiscrepancy: null,
  regressionGuardActive: false,
  fillsFromHandoff: null,
  fillsFromLedger: null,
  fillsUntilReview: null,
  nextReviewAtFills: PROFIT_V1_NEXT_REVIEW_AT_FILLS,
  fillsPerReview: 50,
  improvementId: "bundle-45",
  pendingBundleId: null,
  improvementPhase: "post-improvement",
  lastReviewGateFills: 1584,
  fillsSinceLastImprovement: null,
  atReview: false,
  atFillGate: false,
  atBindingReview: false,
  planDoc: "notes/2026-06-04-profit-v1-review-protocol.md",
  briefLinesKo: [],
  roundTripNetBps: null,
  handoffUpdatedAt: null,
};

/** Dashboard compact polls — enough for review layers without full cohort note replay. */
export const DASHBOARD_ENTRY_OBSERVABILITY_LIMIT = 400;

/** Compact dashboard observability window (review layers + recent progress). */
export function fixedCycleObservabilityLimit(_requiredTrades: number) {
  return DASHBOARD_ENTRY_OBSERVABILITY_LIMIT;
}

export function isAwaitingImprovementShip(
  cycle?: Pick<CycleOperatorSummary, "improvementPhase" | "nextReviewAtFills">,
) {
  if (!cycle) return false;
  return (
    cycle.improvementPhase === "awaiting-improvement-ship" ||
    (cycle.improvementPhase === "post-review" && cycle.nextReviewAtFills == null)
  );
}

/** Observability query sizing — not the operator-facing review gate during ship-wait. */
export function observabilityRequiredTrades(cycle?: CycleOperatorSummary | null) {
  const target = resolveCycleProgressTarget(cycle);
  if (target.requiredTrades > 0) return target.requiredTrades;
  const profitCycle =
    cycle?.protocolVersion === "profit-v1"
      ? cycle
      : cycle === null
        ? PROFIT_V1_DASHBOARD_FALLBACK
        : null;
  if (profitCycle?.fills != null) {
    return profitCycle.fills + (profitCycle.fillsPerReview ?? 50);
  }
  return 200;
}

export function resolveCycleProgressTarget(
  cycle?: CycleOperatorSummary | null,
  entry?: EntryObservabilityResponse,
) {
  const profitCycle =
    cycle?.protocolVersion === "profit-v1"
      ? cycle
      : cycle === null
        ? PROFIT_V1_DASHBOARD_FALLBACK
        : null;

  if (profitCycle?.protocolVersion === "profit-v1") {
    const awaitingShip = isAwaitingImprovementShip(profitCycle);
    const explicitGate = profitCycle.nextReviewAtFills;
    const requiredTrades =
      explicitGate != null && explicitGate > 0
        ? explicitGate
        : awaitingShip
          ? 0
          : profitCycle.fills != null
            ? profitCycle.fills + (profitCycle.fillsPerReview ?? 50)
            : 200;
    return {
      protocolVersion: "profit-v1" as const,
      createdAfter: profitCycle.createdAfter ?? FIXED_CYCLE_CREATED_AFTER,
      requiredTrades,
      label: awaitingShip ? "profit-v1 운영" : "profit-v1 리뷰",
      pendingBundleId: profitCycle.pendingBundleId,
      improvementPhase: profitCycle.improvementPhase,
      operatingMode: profitCycle.operatingMode,
      fillsFromHandoff: profitCycle.fills,
      fillsUntilReview: profitCycle.fillsUntilReview,
      atReview: profitCycle.atReview,
      roundTripNetBps: profitCycle.roundTripNetBps,
      cycleSummary: profitCycle,
    };
  }
  const progress = entry?.naturalTradeProgress;
  const required =
    progress?.requiredTrades && progress.requiredTrades > 0
      ? progress.requiredTrades
      : LEGACY_CYCLE_REQUIRED_TRADES;
  return {
    protocolVersion: "legacy" as const,
    createdAfter: FIXED_CYCLE_CREATED_AFTER,
    requiredTrades: required,
    label: "고정 사이클",
    pendingBundleId: null,
    improvementPhase: null,
    operatingMode: null,
    fillsFromHandoff: null,
    fillsUntilReview: null,
    atReview: false,
    roundTripNetBps: null,
  };
}

export function entryObservabilityRequestPath(cycle?: CycleOperatorSummary | null) {
  const target = resolveCycleProgressTarget(cycle);
  const requiredForQuery = observabilityRequiredTrades(cycle);
  const params = new URLSearchParams({
    limit: String(fixedCycleObservabilityLimit(requiredForQuery)),
    requiredTrades: String(requiredForQuery),
    createdAfter: target.createdAfter,
    compact: "true",
  });
  return `/entry/observability?${params.toString()}`;
}

export type TradeCycleBadgeSummary = {
  tone: "green" | "blue" | "amber" | "rose";
  actualTrades: number;
  requiredTrades: number;
  remainingTrades: number;
  complete: boolean;
  percent: number;
  headline: string;
  detail: string;
  meta?: string;
  label: string;
  protocolVersion: string;
  loading: boolean;
  reviewReadiness?: ReviewReadiness | null;
  readinessLayers?: string | null;
  readinessTone?: "green" | "blue" | "amber" | "rose";
  nextAction?: {
    title: string;
    primary: string;
    gate: string;
    guardrail: string;
  } | null;
};

function formatAgeLabel(refreshedAt?: string, nowMs = Date.now()) {
  if (!refreshedAt) return undefined;
  const ageSec = Math.round((nowMs - Date.parse(refreshedAt)) / 1000);
  if (!Number.isFinite(ageSec) || ageSec < 0) return undefined;
  if (ageSec < 60) return `${ageSec}초 전 갱신`;
  return `${Math.round(ageSec / 60)}분 전 갱신`;
}

export function summarizeTradeCycleBadge(
  entry: EntryObservabilityResponse | undefined,
  options: {
    loading?: boolean;
    refreshedAt?: string;
    stale?: boolean;
    fetchError?: string;
    cycle?: CycleOperatorSummary | null;
  } = {},
): TradeCycleBadgeSummary {
  const target = resolveCycleProgressTarget(options.cycle, entry);
  const reviewReadiness =
    target.protocolVersion === "profit-v1"
      ? resolveReviewReadinessClient({ cycle: options.cycle, entry })
      : null;
  const readinessLayers = formatReviewReadinessLayers(reviewReadiness);
  const progress = entry?.naturalTradeProgress;
  const window = entry?.window;
  const unlinked = entry?.naturalTradeProgress?.excluded?.unlinkedExecution ?? 0;
  const qualityFlags: string[] = [];
  if (window?.noteLimitHit) qualityFlags.push("노트 상한 도달");
  if (unlinked > 0) qualityFlags.push(`미연결 체결 ${unlinked}`);

  const actualFromEntry = progress?.actualTrades;
  const handoffFills = options.cycle?.fillsFromHandoff;
  const ledgerFills = options.cycle?.fillsFromLedger;
  const fillsPendingLedger =
    target.protocolVersion === "profit-v1" &&
    !isAwaitingImprovementShip(options.cycle ?? undefined) &&
    options.cycle?.fillsSource === "handoff_snapshot" &&
    options.cycle?.fillsFromLedger == null;
  let actual = actualFromEntry ?? 0;
  if (target.protocolVersion === "profit-v1") {
    if (options.cycle?.fillsSource === "authoritative_guard" && options.cycle.fills != null) {
      actual = options.cycle.fills;
    } else if (ledgerFills != null) {
      actual = ledgerFills;
    } else if (options.cycle?.fills != null && !fillsPendingLedger) {
      actual = options.cycle.fills;
    } else if (actualFromEntry != null) {
      actual = actualFromEntry;
    } else {
      actual = 0;
    }
  }

  const fillCountKnown =
    target.protocolVersion === "profit-v1"
      ? isAwaitingImprovementShip(options.cycle ?? undefined)
        ? options.cycle?.fills != null || options.cycle?.fillsFromHandoff != null
        : options.cycle?.fillsFromLedger != null ||
          (options.cycle?.fillsSource === "ledger_live" && options.cycle?.fills != null) ||
          (actualFromEntry != null && actualFromEntry > 0)
      : Boolean(progress);

  if ((options.loading || fillsPendingLedger) && (options.cycle === undefined || !fillCountKnown)) {
    const gateUnknown = options.cycle === undefined;
    const required = gateUnknown ? 0 : target.requiredTrades;
    return {
      tone: "blue",
      actualTrades: 0,
      requiredTrades: required,
      remainingTrades: required,
      complete: false,
      percent: 0,
      headline: gateUnknown ? "… / …" : required > 0 ? `… / ${required}` : "… / …",
      detail: "거래 수 불러오는 중",
      meta: options.fetchError ? "API 연결 확인" : undefined,
      label: gateUnknown ? "profit-v1 리뷰" : target.label,
      protocolVersion: gateUnknown ? "profit-v1" : target.protocolVersion,
      loading: true,
      reviewReadiness: null,
      readinessLayers: null,
      readinessTone: "blue",
      nextAction: null,
    };
  }

  const awaitingShip =
    target.protocolVersion === "profit-v1" &&
    (target.improvementPhase === "awaiting-improvement-ship" ||
      (target.improvementPhase === "post-review" && options.cycle?.nextReviewAtFills == null));
  const pendingBundleId = options.cycle?.pendingBundleId ?? null;
  const activeImprovementId = options.cycle?.improvementId ?? null;
  const nextAction =
    awaitingShip && target.protocolVersion === "profit-v1"
      ? {
          title: "지금 개선작업",
          primary: pendingBundleId ? `${pendingBundleId} ship 준비` : "개선 번들 ship 준비",
          gate: `다음 공식 리뷰는 ship 후 +${options.cycle?.fillsPerReview ?? 50} fill`,
          guardrail: "maker/copy-flow는 별도 번들",
        }
      : target.protocolVersion === "profit-v1" &&
          target.improvementPhase === "post-improvement" &&
          activeImprovementId
        ? {
            title: "측정 중",
            primary: `${activeImprovementId} 측정 윈도우`,
            gate: `다음 공식 리뷰 @${target.requiredTrades}`,
            guardrail: "maker/copy-flow는 별도 번들",
          }
        : null;

  const required = target.requiredTrades;
  const remaining = Math.max(0, required - actual);
  const atFillGate = options.cycle?.atFillGate ?? options.cycle?.atReview === true;
  const atBindingGate = options.cycle?.atBindingReview === true;
  const bindingReady = reviewReadiness?.bindingReady === true;
  const atReviewGate =
    target.protocolVersion === "profit-v1" &&
    !awaitingShip &&
    (atBindingGate || (atFillGate && bindingReady));
  const complete =
    target.protocolVersion === "profit-v1" ? bindingReady : Boolean(progress?.complete);

  if (!progress && target.protocolVersion !== "profit-v1") {
    return {
      tone: "amber",
      actualTrades: 0,
      requiredTrades: 0,
      remainingTrades: 0,
      complete: false,
      percent: 0,
      headline: "— / —",
      detail: options.loading ? "거래 수 불러오는 중" : "거래 수 확인 불가",
      meta: options.fetchError ? "API 연결 확인" : undefined,
      label: target.label,
      protocolVersion: target.protocolVersion,
      loading: Boolean(options.loading),
      reviewReadiness,
      readinessLayers,
      readinessTone: reviewReadiness ? reviewReadinessTone(reviewReadiness.status) : "blue",
      nextAction: null,
    };
  }

  const percent = required > 0 ? Math.min(100, Math.round((actual / required) * 100)) : 0;

  const ageLabel = formatAgeLabel(options.refreshedAt);
  const metaParts = [
    options.cycle?.improvementId ? `개선 ${options.cycle.improvementId} enforce` : undefined,
    summarizeBundle20ExitEnforceLine(options.cycle) ?? undefined,
    target.operatingMode ? `모드 ${target.operatingMode}` : undefined,
    target.pendingBundleId ? `대기 ${target.pendingBundleId}` : undefined,
    target.roundTripNetBps != null ? `RT ${target.roundTripNetBps.toFixed(1)} bps` : undefined,
    summarizePortfolioCouplingLine(options.cycle) ?? undefined,
    summarizeBundle4ShadowLine(options.cycle) ?? undefined,
    summarizeBundle5ShadowLine(options.cycle) ?? undefined,
    target.protocolVersion === "profit-v1" && options.cycle?.fillsSource === "authoritative_guard"
      ? `권위 ${options.cycle.fills ?? "?"} · 라이브 ${ledgerFills ?? "?"} (Δ${options.cycle.fillsDiscrepancy ?? "?"})`
      : target.protocolVersion === "profit-v1" &&
          options.cycle?.fillsDiscrepancy != null &&
          options.cycle.fillsDiscrepancy !== 0
        ? `핸드오프 ${handoffFills ?? "?"} · 라이브 ${ledgerFills ?? "?"} (Δ${options.cycle.fillsDiscrepancy})`
        : target.protocolVersion === "profit-v1" &&
            options.cycle?.fillsSource === "handoff_snapshot"
          ? "핸드오프 스냅샷 (cycle:heartbeat 권장)"
          : undefined,
    options.stale ? "갱신 지연" : ageLabel,
    qualityFlags.length ? qualityFlags.join(" · ") : undefined,
    options.fetchError && options.stale ? "마지막 성공 값" : undefined,
  ].filter(Boolean);

  let detail: string;
  if (!fillCountKnown && target.protocolVersion === "profit-v1") {
    detail = "프로토콜 체결 수 확인 중 · cycle:heartbeat";
  } else if (awaitingShip && target.protocolVersion === "profit-v1") {
    const lastGate = options.cycle?.lastReviewGateFills;
    const sinceImprovement = options.cycle?.fillsSinceLastImprovement;
    detail = lastGate != null ? `개선 배포 대기 · @${lastGate} 리뷰 완료` : "개선 배포 대기";
    if (sinceImprovement != null && sinceImprovement > 0) {
      detail += ` · 개선 후 +${sinceImprovement}건`;
    }
  } else if (bindingReady && target.protocolVersion === "profit-v1") {
    detail = reviewReadiness?.detailKo ?? "리뷰 판정 가능";
  } else if (reviewReadiness && target.protocolVersion === "profit-v1") {
    detail = reviewReadiness.detailKo;
  } else if (complete && target.protocolVersion === "profit-v1") {
    detail = "리뷰 게이트 도달";
  } else if (complete) {
    detail = "목표 달성 · CEO 검토 가능";
  } else if (target.protocolVersion === "profit-v1") {
    detail =
      remaining > 0 ? `다음 리뷰까지 ${remaining}건 (@${required})` : `리뷰 게이트 @${required}`;
  } else {
    detail = `남은 ${remaining}건`;
  }

  let headline = `${actual} / ${required}`;
  if (awaitingShip && target.protocolVersion === "profit-v1") {
    headline = `프로토콜 체결 ${actual}`;
  } else if (!fillCountKnown && target.protocolVersion === "profit-v1" && required > 0) {
    headline = `? / ${required}`;
  }

  const readinessStatusTone = reviewReadiness ? reviewReadinessTone(reviewReadiness.status) : null;
  const tone =
    readinessStatusTone === "rose"
      ? "rose"
      : awaitingShip
        ? "amber"
        : complete
          ? "green"
          : readinessStatusTone === "amber"
            ? "amber"
            : percent >= 85
              ? "amber"
              : "blue";

  if (reviewReadiness?.metaKo) {
    metaParts.unshift(reviewReadiness.metaKo);
  }

  return {
    tone,
    actualTrades: actual,
    requiredTrades: required,
    remainingTrades: remaining,
    complete,
    percent,
    headline,
    detail,
    meta: metaParts.length ? metaParts.join(" · ") : ageLabel,
    label: target.label,
    protocolVersion: target.protocolVersion,
    loading: false,
    reviewReadiness,
    readinessLayers,
    readinessTone: readinessStatusTone ?? tone,
    nextAction,
  };
}

/** @deprecated use resolveCycleProgressTarget — kept for imports */
export const FIXED_CYCLE_REQUIRED_TRADES = LEGACY_CYCLE_REQUIRED_TRADES;
